<template>
  <div>
    <p class="tw-text-muted tw-mb-4 tw-text-sm">
      Live sync / purge / reconcile runs and recent operational alerts.
    </p>

    <div class="kpi-row tw-mb-4">
      <div class="kpi-card">
        <div class="kpi-label">Running</div>
        <div class="kpi-value">{{ runningCountLabel }}</div>
        <div class="kpi-hint">Active jobs right now</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-label">Alerts</div>
        <div class="kpi-value">{{ alerts.length }}</div>
        <div class="kpi-hint">Recent operational notices</div>
      </div>
    </div>

    <Card class="tw-mb-4">
      <template #title>Running jobs</template>
      <template #subtitle>Cancel in-flight work if a feed is stuck or misconfigured</template>
      <template #content>
        <jobs-consumer v-slot="{ runningJobs }">
          <div class="page-table">
            <div class="page-head">
              <span>Source</span>
              <span>Type</span>
              <span>Status</span>
              <span>Progress</span>
              <span>Started</span>
              <span />
            </div>
            <div v-if="!runningJobs.length" class="page-empty tw-text-muted">No jobs running</div>
            <div v-for="(job, index) of runningJobs" :key="index" class="page-row">
              <router-link :to="`/sources/${job.sourceName}`" class="tw-font-semibold tw-text-black">
                {{ job.sourceName }}
              </router-link>
              <span>{{ job.type }}</span>
              <Chip :label="job.status || 'running'" :class="job.status === 'cancelling' ? 'p-chip-warn' : ''" />
              <div class="tw-text-sm tw-text-muted">
                <div>{{ (job.progress && job.progress.message) || '—' }}</div>
                <div v-if="job.progress && job.progress.resourceName" class="tw-text-xs">
                  {{ job.progress.resourceName }}
                  <span v-if="job.progress.page"> · page {{ job.progress.page }}</span>
                </div>
              </div>
              <display-datetime :datetime="job.startedAt" />
              <Button
                label="Cancel"
                icon="pi pi-times"
                class="p-button-outlined p-button-danger p-button-sm"
                :loading="cancellingKey === jobKey(job)"
                :disabled="job.status === 'cancelling'"
                @click="cancel(job)"
              />
            </div>
          </div>
        </jobs-consumer>
      </template>
    </Card>

    <Card>
      <template #title>Recent alerts</template>
      <template #subtitle>Newest first · live subscription</template>
      <template #content>
        <div v-if="!alerts.length" class="tw-text-muted">No alerts yet.</div>
        <div v-else class="tw-flex tw-flex-col tw-gap-2">
          <Message
            v-for="alert in alerts"
            :key="alert.id"
            :severity="alertSeverity(alert.level)"
            :closable="false"
          >
            <div class="tw-font-semibold">{{ alert.title }}</div>
            <div>{{ alert.message }}</div>
            <div v-if="alert.sourceName" class="tw-text-xs tw-opacity-80 tw-mt-1">
              {{ alert.sourceName }} · {{ alert.jobType }} ·
              <display-datetime :datetime="alert.createdAt" />
            </div>
          </Message>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import gql from 'graphql-tag'

export default {
  inject: {
    jobsProvider: { default: null },
  },
  data() {
    return {
      alerts: [],
      cancellingKey: null,
    }
  },
  computed: {
    runningCountLabel() {
      if (!this.jobsProvider || this.jobsProvider.loading || this.jobsProvider.error) return '…'
      return this.jobsProvider.runningJobs.length
    },
  },
  apollo: {
    alerts: {
      query: gql`query {
        alerts: recentAlerts {
          id
          createdAt
          level
          title
          message
          sourceName
          jobType
        }
      }`,
      fetchPolicy: 'no-cache',
    },
    $subscribe: {
      recentAlerts: {
        query: gql`subscription {
          recentAlerts {
            id
            createdAt
            level
            title
            message
            sourceName
            jobType
          }
        }`,
        result({ data }) {
          if (data && data.recentAlerts) {
            this.alerts = data.recentAlerts
          }
        },
      },
    },
  },
  methods: {
    jobKey(job) {
      return `${job.sourceName}:${job.type}`
    },
    alertSeverity(level) {
      if (level === 'danger') return 'error'
      if (level === 'success') return 'success'
      if (level === 'warn' || level === 'warning') return 'warn'
      return 'info'
    },
    async cancel(job) {
      this.cancellingKey = this.jobKey(job)
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($job: JobInput!) {
            cancelJob(job: $job)
          }`,
          variables: {
            job: {
              sourceName: job.sourceName,
              type: job.type,
            },
          },
        })
        this.$toast.add({
          severity: 'info',
          summary: 'Cancel requested',
          detail: `${job.type} · ${job.sourceName}`,
          life: 3000,
        })
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Cancel failed',
          detail: error.message,
          life: 5000,
        })
      } finally {
        this.cancellingKey = null
      }
    },
  },
}
</script>

<style scoped>
.kpi-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.kpi-card {
  padding: 1rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.kpi-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #71717a;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  margin-top: 0.15rem;
  color: #18181b;
}

.kpi-hint {
  font-size: 0.75rem;
  color: #a1a1aa;
  margin-top: 0.2rem;
}

.page-table {
  display: flex;
  flex-direction: column;
}

.page-head,
.page-row {
  display: grid;
  grid-template-columns: 1.2fr 0.7fr 0.8fr 1.6fr 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f4f4f5;
  font-size: 0.875rem;
}

.page-head {
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom-color: #e4e4e7;
}

.page-empty {
  padding: 1rem 0;
}

@media (max-width: 900px) {
  .page-head {
    display: none;
  }
  .page-row {
    grid-template-columns: 1fr;
  }
}
</style>
